import { computeDestinationPoint } from "geolib";
import { writable } from "svelte/store";
import {
    directions,
    fetchCannedRoute,
    origin as fetchOrigin,
    poi
} from "../api/api.js";
import { sleep } from "../helpers/time.js";
import persisted from "./common/persisted.js";

let progress = 0.0;

const DEFAULT_ORIGIN = { latitude: 39.8282, longitude: -98.5795 };

export const DESTINATIONS_WEST = [
    { latitude: 64, longitude: -147 },
    { latitude: 61, longitude: -150 },
    { latitude: 48, longitude: -124 },
    { latitude: 48, longitude: -110 },
    { latitude: 48, longitude: -100 },
    { latitude: 48, longitude: -90 },
    { latitude: 47, longitude: -124 },
    { latitude: 46, longitude: -124 },
    { latitude: 45, longitude: -124 },
    { latitude: 44, longitude: -124 },
    { latitude: 43, longitude: -124 },
    { latitude: 42, longitude: -124 },
    { latitude: 41, longitude: -124 },
    { latitude: 40, longitude: -124 },
    { latitude: 39, longitude: -123 },
    { latitude: 38, longitude: -123 },
    { latitude: 37, longitude: -122 },
    { latitude: 36, longitude: -121 },
    { latitude: 35, longitude: -120 },
    { latitude: 34, longitude: -118 },
    { latitude: 33, longitude: -117 }
];

export const DESTINATIONS_EAST = [
    { latitude: 44, longitude: -70 },
    { latitude: 43, longitude: -71 },
    { latitude: 42, longitude: -71 },
    { latitude: 41, longitude: -74 },
    { latitude: 40, longitude: -75 },
    { latitude: 39, longitude: -76 },
    { latitude: 38, longitude: -77 },
    { latitude: 37, longitude: -77 },
    { latitude: 36, longitude: -77 },
    { latitude: 35, longitude: -77 },
    { latitude: 34, longitude: -79 },
    { latitude: 33, longitude: -80 },
    { latitude: 32, longitude: -81 },
    { latitude: 31, longitude: -81 },
    { latitude: 30, longitude: -81 },
    { latitude: 29, longitude: -82 },
    { latitude: 28, longitude: -82 },
    { latitude: 27, longitude: -82 },
    { latitude: 26, longitude: -81 },
    { latitude: 27, longitude: -97 },
    { latitude: 30, longitude: -90 },
    { latitude: 30, longitude: -105 }
];

function reportProgress(increment, description) {
    if (typeof CustomEvent === "undefined") {
        return;
    }

    progress = Math.max(Math.min(progress + increment, 1.0), progress);

    const event = new CustomEvent("route:progress", {
        detail: { progress, description }
    });

    window.dispatchEvent(event);
}

function destinationFrom(from) {
    const shouldTravelWest =
        from.longitude === -98.5795
            ? Math.random() < 0.5
            : from.longitude > -98.0;

    const locations = shouldTravelWest ? DESTINATIONS_WEST : DESTINATIONS_EAST;

    let destination = locations[Math.trunc(Math.random() * locations.length)];

    destination = computeDestinationPoint(
        destination,
        500_000,
        Math.random() * 360.0
    );

    return destination;
}

async function getDirections(waypoints, overview) {
    if (waypoints.length > 25) {
        console.error("too many points", waypoints.length);
        return;
    }

    const encodedWaypoints = waypoints
        .map(({ latitude, longitude }) => `${longitude},${latitude}`)
        .join(";");

    try {
        const result = await directions(encodedWaypoints, overview, [
            "motorway"
        ]);

        if (result.directions === null) {
            throw new Error("failed to get directions", waypoints, overview);
        }

        return result.directions;
    } catch (error) {
        throw error;
    }
}

async function getPointOfInterest(coordinate, query) {
    await sleep(1000);

    try {
        const result = await poi(query, coordinate);

        return result.poi;
    } catch (error) {
        console.warn("failed at getPointOfInterest", error);
        return null;
    }
}

function createScatteredRoute(route, frequency, amplitude) {
    let result = [];

    for (
        let i = 1;
        i < route.length - 2;
        i += Math.ceil((route.length - 2) / 25)
    ) {
        if (Math.random() < frequency) {
            const waypoint = computeDestinationPoint(
                route[i],
                Math.random() * amplitude,
                Math.random() * 360
            );

            result.push(waypoint);
        } else {
            result.push(route[i]);
        }
    }

    result = [route[0], ...result.slice(0, 23), route.at(-1)];

    return result;
}

async function createRoughRoute(points) {
    let roughRoute = null;
    let attemptCount = 0;

    while (roughRoute === null && attemptCount < 5) {
        attemptCount += 1;

        try {
            roughRoute = await getDirections(points, "simplified");
        } catch (error) {
            console.warn("failed to get simplified directions", error);
            continue;
        }
    }

    if (!roughRoute) {
        throw new Error("failed at createRoughRoute");
    }

    return roughRoute;
}

async function createDetailedRoute(points) {
    let detailedRoute = null;
    let attemptCount = 0;

    while (detailedRoute === null && attemptCount < 5) {
        attemptCount += 1;

        try {
            detailedRoute = await getDirections(points, "full");
        } catch (error) {
            console.warn("failed to get detailed directions", error);
            continue;
        }
    }

    if (!detailedRoute) {
        throw new Error("failed at createDetailedRoute");
    }

    return detailedRoute;
}

async function createRoughScatteredRoute(roughRoute, profile) {
    let roughScatteredRoute = null;
    let attemptCount = 0;

    while (roughScatteredRoute === null && attemptCount < 10) {
        attemptCount += 1;

        const scatteredRoute = createScatteredRoute(
            roughRoute,
            profile.wandering.frequency,
            profile.wandering.amplitude
        );

        try {
            roughScatteredRoute = await getDirections(
                scatteredRoute,
                "simplified"
            );
        } catch (error) {
            console.warn("failed to get directions for scattered route");
            continue;
        }
    }

    if (!roughScatteredRoute) {
        throw new Error("failed at createRoughScatteredRoute");
    }

    return roughScatteredRoute;
}

async function pointsOfInterestForRoute(route, profile) {
    const result = [route[0]];

    for (
        let i = 0;
        i < route.length;
        i += Math.ceil(route.length / profile.interests.rate)
    ) {
        const location = route[Math.trunc(i)];

        const query =
            profile.interests.queries[
                Math.trunc(Math.random() * profile.interests.queries.length)
            ];

        try {
            const point = await getPointOfInterest(location, query);

            if (point) {
                result.push(point);
            }
        } catch (error) {
            console.warn("failed to get poi");
        }

        reportProgress(
            0.7 / profile.interests.rate,
            "Picking points of interest"
        );
    }

    return result.slice(0, 25);
}

export async function createRoute(from, profile, to) {
    let route = undefined;
    let attemptCount = 0;

    while (route === undefined && attemptCount < 5) {
        attemptCount += 1;
        progress = 0.01;

        if (!to) {
            to = destinationFrom(from);
        }

        let roughRoute, roughScatteredRoute, pointsOfInterest;
        reportProgress(0.0, "Setting up your journey");

        try {
            roughRoute = await createRoughRoute([from, to]);
            reportProgress(0.12, "Determing general direction");
        } catch (error) {
            console.warn("failed at createRoughRoute");
            continue;
        }

        try {
            roughScatteredRoute = await createRoughScatteredRoute(
                roughRoute,
                profile
            );
            reportProgress(0.08, "Finding waypoints");
        } catch (error) {
            console.warn("createRoughScatteredRoute", to);
            continue;
        }

        try {
            pointsOfInterest = await pointsOfInterestForRoute(
                roughScatteredRoute,
                profile
            );
        } catch (error) {
            console.warn("failed at pointsOfInterest");
            continue;
        }

        try {
            route = await createDetailedRoute(pointsOfInterest, profile);
        } catch (error) {
            console.warn("failed at createDetailedRoute");
            continue;
        }
    }

    reportProgress(0.1, "Calculating optimal route");

    if (!route) {
        console.warn("failed to create route", attemptCount);
    }

    reportProgress(Number.MAX_SAFE_INTEGER, "Almost done");

    return route;
}

export async function createInitialRoute(profile) {
    let attemptCount = 0;
    let route;

    while (!route && attemptCount < 5) {
        attemptCount += 1;

        try {
            const { origin } = await fetchOrigin();
            route = await createRoute(origin, profile);
        } catch (error) {
            console.warn(`failed to create a route (${error})`);
            continue;
        }
    }

    if (route) {
        return route;
    }

    return createRoute(DEFAULT_ORIGIN, profile);
}

function originFromQuery() {
    if (typeof window === "undefined") {
        return undefined;
    }

    const params = new URLSearchParams(window.location.search);
    const latitude = Number(params.get("lat"));
    const longitude = Number(params.get("lon"));

    if (
        Number.isFinite(latitude) === false ||
        Number.isFinite(longitude) === false ||
        latitude === 0 ||
        longitude === 0
    ) {
        return undefined;
    }

    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
        return undefined;
    }

    return { latitude, longitude };
}

function createRouteStore() {
    const { set, subscribe } = writable(undefined);

    return {
        subscribe,
        set,

        async createRoute(from, profile) {
            try {
                if (from) {
                    const nextRoute = await createRoute(from, profile);

                    if (nextRoute === undefined) {
                        return false;
                    }

                    set(nextRoute);
                    return true;
                }

                const nextRoute = await createInitialRoute(profile);

                if (nextRoute === undefined) {
                    return false;
                }

                set(nextRoute);
                return true;
            } catch (error) {
                console.warn(`failed to create a route (${error})`);
                return false;
            }
        },

        async createRouteFromQuery(profile) {
            const origin = originFromQuery();

            if (origin === undefined) {
                return false;
            }

            return this.createRoute(origin, profile);
        },

        async fetchCannedRoute() {
            try {
                const { route } = await fetchCannedRoute();
                set(route);
            } catch (error) {
                console.warn(`failed to create a route (${error})`);
            }
        }
    };
}

const route = persisted("route:v10", true, createRouteStore());

export default route;
